import { CheckIcon, ChevronsUpDownIcon, KeyRoundIcon } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { WorkspaceEnvVar } from "@/queries/env-vars";

interface WorkspaceEnvVarSelectProps {
  value: string;
  envVars: WorkspaceEnvVar[];
  workspaceId: number;
  isLoading: boolean;
  onChange: (value: string) => void;
}

const WorkspaceEnvVarSelect = ({
  value,
  envVars,
  workspaceId,
  isLoading,
  onChange,
}: WorkspaceEnvVarSelectProps) => {
  const [open, setOpen] = useState(false);
  const selectedEnvVar = envVars.find(envVar => envVar.key === value);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="w-full justify-between font-normal"
        onClick={() => setOpen(true)}
      >
        <span className="flex min-w-0 items-center gap-2">
          <KeyRoundIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate">
            {selectedEnvVar?.key || value || "Select environment variable"}
          </span>
        </span>
        <ChevronsUpDownIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
      </Button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Search workspace environment variables..." />
        <CommandList>
          <CommandEmpty>
            <div className="space-y-2">
              <p>
                {isLoading ? "Loading environment variables..." : "No environment variables found."}
              </p>
              {!isLoading && (
                <a
                  href={`/workspace/${workspaceId}/settings/env-vars`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Manage Environment Variables
                </a>
              )}
            </div>
          </CommandEmpty>
          <CommandGroup heading="Workspace Environment Variables">
            {envVars.map(envVar => (
              <CommandItem
                key={envVar.id}
                value={envVar.key}
                onSelect={() => {
                  onChange(envVar.key);
                  setOpen(false);
                }}
              >
                <CheckIcon
                  className={envVar.key === value ? "h-4 w-4 opacity-100" : "h-4 w-4 opacity-0"}
                />
                <span className="font-mono">{envVar.key}</span>
                {envVar.is_empty && (
                  <span className="ml-auto text-xs text-amber-600">Value not set</span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
};

export default WorkspaceEnvVarSelect;
